<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import NavBar from '@/components/common/layout/NavBar.vue'
import DynamicBanners from '@/components/common/layout/DynamicBanners.vue'
import PersonnalBar from '@/components/common/layout/PersonnalBar.vue'
import NewsModal from '@/components/common/layout/NewsModal.vue'
import NotificationsManager from '@/components/common/feedback/NotificationsManager.vue'
import TricksterBanner from '@/components/common/layout/TricksterBanner.vue'
import ApyLayout from '@/v2/components/layout/ApyLayout.vue'
import OmnisearchOverlay from '@/v2/components/omnisearch/OmnisearchOverlay.vue'
import HelpDrawer from '@/components/common/help/HelpDrawer.vue'
import { useOmnisearchStore } from '@/stores/useOmnisearchStore'
import { useLayoutModeStore } from '@/stores/useLayoutModeStore'
import { useHelpRouteSync } from '@/composables/useHelpRouteSync'

const route = useRoute()
const omnisearchStore = useOmnisearchStore()
const layoutModeStore = useLayoutModeStore()

// Alimente la clé d'aide contextuelle depuis la route (V1 et V2).
useHelpRouteSync()

const hideRightSidebar = computed(() => route.matched.some((r) => r.meta.hideRightSidebar))
const isHomePage = computed(() => route.name === 'home')
const isFullscreenPage = computed(() => route.matched.some((r) => r.meta.isPublic) || route.name === 'logout')

// Routes mixtes V1/V2 : le shell est choisi par le mode utilisateur, plus par la route.
// En mode v2 (opt-in user + BU éligible via `effectiveMode`), ApyLayout héberge aussi
// les pages V1 ; le guard router continue de réserver les routes `meta.version: 'v2'`
// au mode v2, donc pas besoin de revérifier l'éligibilité ici.
const isV2Shell = computed(() => layoutModeStore.effectiveMode === 'v2')

// ── Ctrl+K global — fonctionne en V1 et V2 ──────────────────────────────────
function onGlobalKeydown(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    omnisearchStore.toggle()
  }
}

onMounted(() => document.addEventListener('keydown', onGlobalKeydown))
onUnmounted(() => document.removeEventListener('keydown', onGlobalKeydown))
</script>

<template>
  <!-- Fullscreen layout for /auth/login, /auth/forgot-password, /auth/reset, /auth/logout -->
  <template v-if="isFullscreenPage">
    <RouterView />
  </template>

  <!-- V2 layout (mode v2 = opt-in user + BU éligible) — héberge pages V1 et V2 -->
  <template v-else-if="isV2Shell">
    <ApyLayout />
    <NotificationsManager />
  </template>

  <!-- Standard layout with nav + sidebar -->
  <template v-else>
    <TricksterBanner />
    <div class="flex min-h-screen flex-col">
      <div class="drawer w-full flex-1 lg:drawer-open">
        <input id="my-drawer-2" type="checkbox" class="drawer-toggle" />
        <div class="drawer-content container flex w-full flex-col lg:max-w-full">
          <DynamicBanners />
          <div class="flex w-full flex-col lg:flex-row lg:items-start">
            <div class="min-w-0 flex-1" :class="hideRightSidebar ? 'w-full' : ''">
              <RouterView />
            </div>
            <aside
              v-if="!hideRightSidebar && isHomePage"
              class="order-last w-full shrink-0 border-t border-base-300 bg-base-100/80 lg:sticky lg:top-0 lg:z-10 lg:max-h-screen lg:w-72 lg:overflow-y-auto lg:border-t-0 lg:border-l lg:bg-transparent"
            >
              <PersonnalBar />
            </aside>
          </div>
        </div>
        <NavBar />
        <NewsModal />
      </div>
      <NotificationsManager />
    </div>
  </template>

  <!-- OmniSearch overlay — disponible partout (V1 et V2), rendu via Teleport to="body" -->
  <OmnisearchOverlay />

  <!-- Aide contextuelle (USER_MASTER) — disponible partout (V1 et V2) sauf pages plein écran -->
  <HelpDrawer v-if="!isFullscreenPage" />
</template>
